import { useMemo, useState } from 'react'
import { adminApi } from '../../services/api'

interface WhatsAppPanelProps {
  orderId: string
  customerName: string
  customerPhone: string
  orderNumber: string
  orderStatus: string
  agentName?: string
  agentId?: string
  paymentMode?: string | null
}

interface WhatsAppPreview {
  url: string
  message: string
  phone: string
}

const WHATSAPP_GREEN = '#25D366'
const WHATSAPP_GREEN_DARK = '#1EAD54'

const buildWhatsAppUrl = (phone: string, message: string) => {
  const cleanPhone = phone.replace(/[\s\-\+\(\)]/g, '')
  const phoneWithCountry = cleanPhone.startsWith('91') ? cleanPhone : `91${cleanPhone}`
  return `https://wa.me/${phoneWithCountry}?text=${encodeURIComponent(message)}`
}

const defaultGreeting = (name?: string) => `Hi ${name || 'there'}! This is Rapidry Admin.`

export default function WhatsAppPanel({
  orderId,
  customerName,
  customerPhone,
  orderNumber,
  orderStatus,
  agentId,
  paymentMode,
}: WhatsAppPanelProps) {
  const [customMessage, setCustomMessage] = useState('')
  const [preview, setPreview] = useState<WhatsAppPreview | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const normalizedStatus = orderStatus.toLowerCase()
  const confirmationTemplate = paymentMode === 'cod' ? 'order_confirmed_cod' : 'order_confirmed_online'

  const quickActions = useMemo(() => {
    const actions: Array<{
      key: string
      label: string
      template: string
      target: 'customer' | 'agent'
      requiresAgent?: boolean
    }> = []

    if (normalizedStatus === 'placed') {
      actions.push(
        {
          key: 'order_confirmed',
          label: '📱 Send Order Confirmation',
          template: confirmationTemplate,
          target: 'customer',
        },
        {
          key: 'welcome_customer',
          label: '📱 Welcome New Customer',
          template: 'welcome_new_customer',
          target: 'customer',
        },
      )
    }

    if (normalizedStatus === 'agent_assigned') {
      actions.push(
        {
          key: 'agent_assigned',
          label: '📱 Notify Agent Assigned',
          template: 'agent_assigned',
          target: 'customer',
        },
        {
          key: 'agent_pickup_assignment',
          label: '📱 Send to Agent',
          template: 'agent_pickup_assignment',
          target: 'agent',
          requiresAgent: true,
        },
      )
    }

    if (normalizedStatus === 'picked_up') {
      actions.push({
        key: 'picked_up',
        label: '📱 Confirm Pickup',
        template: 'picked_up',
        target: 'customer',
      })
    }

    if (['at_facility', 'in_wash', 'in_iron', 'quality_check', 'ready'].includes(normalizedStatus)) {
      actions.push({
        key: 'facility_processing',
        label: '📱 Processing Update',
        template: 'processing',
        target: 'customer',
      })
    }

    if (normalizedStatus === 'out_for_delivery') {
      actions.push(
        {
          key: 'out_for_delivery',
          label: '📱 Delivery Update',
          template: 'out_for_delivery',
          target: 'customer',
        },
        {
          key: 'agent_delivery_assignment',
          label: '📱 Send to Agent',
          template: 'agent_delivery_assignment',
          target: 'agent',
          requiresAgent: true,
        },
      )
    }

    if (normalizedStatus === 'delivered') {
      actions.push(
        {
          key: 'delivered',
          label: '📱 Delivery Confirmation',
          template: 'delivered',
          target: 'customer',
        },
        {
          key: 'feedback_request',
          label: '📱 Request Feedback',
          template: 'feedback_request',
          target: 'customer',
        },
        {
          key: 'google_review',
          label: '📱 Send Google Review Link',
          template: 'google_review',
          target: 'customer',
        },
      )
    }

    actions.push(
      {
        key: 'payment_reminder',
        label: '📱 Payment Reminder',
        template: 'payment_reminder_cod',
        target: 'customer',
      },
      {
        key: 'pickup_delay',
        label: '📱 Pickup Delay',
        template: 'pickup_delay',
        target: 'customer',
      },
      {
        key: 'delivery_delay',
        label: '📱 Delivery Delay',
        template: 'delivery_delay',
        target: 'customer',
      },
    )

    return actions
  }, [normalizedStatus, confirmationTemplate])

  const openPreview = (payload: WhatsAppPreview) => {
    setPreview(payload)
  }

  const handleTemplateClick = async (template: string, target: 'customer' | 'agent') => {
    setIsLoading(true)
    setError('')

    try {
      if (target === 'agent') {
        if (!agentId) {
          setError('No agent assigned to this order yet.')
          return
        }

        const response = await adminApi.getAgentWhatsAppLink(agentId, {
          template,
          order_id: orderId,
        })
        const payload = response?.data ?? response
        openPreview({
          url: payload.url,
          message: payload.message,
          phone: payload.phone,
        })
        return
      }

      const response = await adminApi.getWhatsAppLink(orderId, { template })
      const payload = response?.data ?? response
      openPreview({
        url: payload.url,
        message: payload.message,
        phone: payload.phone,
      })
    } catch (err) {
      console.error(err)
      setError('Unable to generate WhatsApp link. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCustomSend = async () => {
    const trimmed = customMessage.trim()
    if (!trimmed) {
      setError('Enter a custom message first.')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const response = await adminApi.getWhatsAppLink(orderId, { custom_message: trimmed })
      const payload = response?.data ?? response
      openPreview({
        url: payload.url,
        message: payload.message,
        phone: payload.phone,
      })
    } catch (err) {
      console.error(err)
      setError('Unable to generate WhatsApp link. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyMessage = async () => {
    if (!preview) {
      return
    }

    try {
      await navigator.clipboard.writeText(preview.message)
    } catch (err) {
      console.error(err)
      setError('Unable to copy message. Please copy manually.')
    }
  }

  const handleOpenWhatsApp = () => {
    if (!preview) {
      return
    }

    const url = buildWhatsAppUrl(preview.phone, preview.message)
    window.open(url, '_blank')
  }

  return (
    <section
      style={{
        margin: '0 24px 24px',
        background: '#FFFFFF',
        borderRadius: '14px',
        border: '1px solid rgba(37,211,102,0.2)',
        borderTop: `3px solid ${WHATSAPP_GREEN}`,
        padding: '16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <span style={{ fontSize: '18px' }}>💬</span>
        <div>
          <p
            className="font-body"
            style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#0F2E2A' }}
          >
            WhatsApp Messages
          </p>
          <p className="font-body" style={{ margin: 0, fontSize: '11px', color: '#9CAB9A' }}>
            #{orderNumber} · {customerName}
          </p>
        </div>
      </div>

      {error ? (
        <div
          className="font-body"
          style={{
            marginBottom: '12px',
            background: '#FEF2F2',
            border: '1px solid #FCA5A5',
            color: '#991B1B',
            padding: '8px 10px',
            borderRadius: '10px',
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          {error}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
        {quickActions.map((action) => {
          const disabled = Boolean(action.requiresAgent && !agentId)
          const isPrimary = action.target === 'customer'

          return (
            <button
              key={action.key}
              type="button"
              className="font-body"
              onClick={() => {
                void handleTemplateClick(action.template, action.target)
              }}
              disabled={disabled || isLoading}
              style={{
                height: '36px',
                padding: '0 12px',
                borderRadius: '10px',
                border: `1.5px solid ${WHATSAPP_GREEN}`,
                background: isPrimary ? WHATSAPP_GREEN : 'transparent',
                color: isPrimary ? '#FFFFFF' : WHATSAPP_GREEN,
                fontSize: '12px',
                fontWeight: 600,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
              onMouseEnter={(event) => {
                if (disabled) {
                  return
                }
                if (isPrimary) {
                  event.currentTarget.style.background = WHATSAPP_GREEN_DARK
                } else {
                  event.currentTarget.style.background = 'rgba(37,211,102,0.1)'
                }
              }}
              onMouseLeave={(event) => {
                if (disabled) {
                  return
                }
                event.currentTarget.style.background = isPrimary ? WHATSAPP_GREEN : 'transparent'
              }}
            >
              {action.label}
            </button>
          )
        })}
      </div>

      <div
        style={{
          borderRadius: '10px',
          border: '1px solid #EAE4D8',
          padding: '12px',
          background: '#F7F5F0',
          marginBottom: '12px',
        }}
      >
        <p
          className="font-body"
          style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: 600, color: '#0F2E2A' }}
        >
          Custom Message
        </p>
        <textarea
          value={customMessage}
          onChange={(event) => setCustomMessage(event.target.value)}
          placeholder="Type a custom message to send via WhatsApp..."
          className="font-body"
          style={{
            width: '100%',
            minHeight: '72px',
            borderRadius: '8px',
            border: '1.5px solid #EAE4D8',
            padding: '10px',
            fontSize: '12px',
            color: '#0F2E2A',
            resize: 'vertical',
            outline: 'none',
          }}
        />
        <button
          type="button"
          className="font-body"
          onClick={() => {
            void handleCustomSend()
          }}
          style={{
            marginTop: '10px',
            height: '36px',
            padding: '0 14px',
            borderRadius: '10px',
            border: `1.5px solid ${WHATSAPP_GREEN}`,
            background: 'transparent',
            color: WHATSAPP_GREEN,
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
          onMouseEnter={(event) => {
            event.currentTarget.style.background = 'rgba(37,211,102,0.1)'
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = 'transparent'
          }}
        >
          📱 Send Custom
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '14px' }}>ℹ️</span>
        <p className="font-body" style={{ margin: 0, fontSize: '11px', color: '#9CAB9A' }}>
          Messages are sent via WhatsApp Web/App. Click to open and send.
        </p>
      </div>
      <p className="font-body" style={{ margin: '6px 0 0', fontSize: '11px', color: '#9CAB9A' }}>
        ℹ️ Messages open in your WhatsApp. You need to press Send.
      </p>

      {preview ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            zIndex: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => setPreview(null)}
        >
          <div
            style={{
              width: 'min(520px, 90vw)',
              background: '#FFFFFF',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0px 24px 64px rgba(15,46,42,0.2)',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <h3
              className="font-display"
              style={{ margin: '0 0 10px', fontSize: '18px', fontWeight: 700, color: '#0F2E2A' }}
            >
              WhatsApp Preview
            </h3>
            <p className="font-body" style={{ margin: '0 0 12px', fontSize: '12px', color: '#9CAB9A' }}>
              To: {preview.phone || customerPhone}
            </p>
            <textarea
              value={preview.message}
              onChange={(event) => setPreview((prev) => (prev ? { ...prev, message: event.target.value } : prev))}
              className="font-body"
              style={{
                width: '100%',
                minHeight: '160px',
                borderRadius: '12px',
                border: '1.5px solid #EAE4D8',
                padding: '12px',
                fontSize: '13px',
                color: '#0F2E2A',
                resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button
                type="button"
                className="font-body"
                onClick={handleCopyMessage}
                style={{
                  flex: 1,
                  height: '40px',
                  borderRadius: '10px',
                  border: '1.5px solid #EAE4D8',
                  background: 'transparent',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#0F2E2A',
                  cursor: 'pointer',
                }}
              >
                Copy Message
              </button>
              <button
                type="button"
                className="font-body"
                onClick={handleOpenWhatsApp}
                style={{
                  flex: 1,
                  height: '40px',
                  borderRadius: '10px',
                  border: 'none',
                  background: WHATSAPP_GREEN,
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#FFFFFF',
                  cursor: 'pointer',
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = WHATSAPP_GREEN_DARK
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = WHATSAPP_GREEN
                }}
              >
                Open WhatsApp
              </button>
            </div>
            <button
              type="button"
              className="font-body"
              onClick={() => setPreview(null)}
              style={{
                marginTop: '12px',
                width: '100%',
                height: '38px',
                borderRadius: '10px',
                border: '1.5px solid #EAE4D8',
                background: 'transparent',
                fontSize: '12px',
                fontWeight: 600,
                color: '#4A5568',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </section>
  )
}

export const buildQuickGreeting = (name?: string, phone?: string) => ({
  phone,
  message: defaultGreeting(name),
})
